import { useState, useEffect } from "react";
import { ScrollView, Pressable, ActivityIndicator, Modal } from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams } from "expo-router";
import {
  doc,
  onSnapshot,
  collection,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useConfirm } from "@/hooks/useConfirm";
import { useAlert } from "@/hooks/useAlert";
import { colors, radius, font, spacing } from "@/lib/theme";
import ContentWidth, { ModalContent } from "@/components/ContentWidth";
import ConfirmSheet from "@/components/ConfirmSheet";
import AlertSheet from "@/components/AlertSheet";
import type { Match, Fee, PaymentMethod } from "@/lib/types";

/* ── helpers ─────────────────────────────────────────── */

function pence(amount: number) {
  return `\u00A3${(amount / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatShortDate(ts: Timestamp | null): string {
  if (!ts) return "";
  try {
    return ts.toDate().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

/* ── styled ──────────────────────────────────────────── */

const Screen = styled.ScrollView`
  flex: 1;
  background-color: ${colors.bg};
`;

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
  padding: 24px;
`;

const EmptyText = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  text-align: center;
`;

const MatchHeader = styled.View`
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 20px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const TeamBadge = styled.View`
  background-color: ${colors.bg};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
  border-width: 1px;
  border-color: ${colors.line};
  align-self: flex-start;
  margin-bottom: 8px;
`;

const TeamBadgeText = styled.Text`
  font-size: 12px;
  font-weight: ${font.bold};
  color: ${colors.inkSecondary};
`;

const MatchTitle = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
`;

const MatchMeta = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const SummaryCard = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding: 16px;
  margin: 20px 20px 0;
`;

const SummaryText = styled.Text`
  font-size: 14px;
  font-weight: ${font.semibold};
  color: ${colors.inkSecondary};
  line-height: 20px;
`;

const SummaryBold = styled.Text`
  font-weight: ${font.extrabold};
  color: ${colors.ink};
`;

const ProgressBarOuter = styled.View`
  height: 10px;
  background-color: ${colors.line};
  border-radius: ${radius.full}px;
  margin-top: 12px;
  overflow: hidden;
`;

const ProgressBarInner = styled.View<{ pct: number }>`
  height: 100%;
  width: ${(p) => Math.min(p.pct, 100)}%;
  background-color: ${colors.brand};
  border-radius: ${radius.full}px;
`;

const Section = styled.View`
  padding-horizontal: 20px;
  margin-top: 20px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const Card = styled.View`
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ hasBorder: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  border-bottom-width: ${(p) => (p.hasBorder ? 1 : 0)}px;
  border-bottom-color: ${colors.line};
`;

const Avatar = styled.View<{ bg: string }>`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  background-color: ${(p) => p.bg};
`;

const AvatarText = styled.Text<{ color: string }>`
  font-size: 13px;
  font-weight: ${font.extrabold};
  color: ${(p) => p.color};
`;

const NameText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const Flex1 = styled.View`
  flex: 1;
`;

const AmountBadge = styled.View`
  background-color: ${colors.amber50};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
  border-width: 1px;
  border-color: ${colors.amber200};
  margin-right: 8px;
`;

const AmountBadgeText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.amber};
`;

const MarkPaidBtn = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding-horizontal: 12px;
  padding-vertical: 6px;
`;

const MarkPaidBtnText = styled.Text`
  color: #fff;
  font-size: 13px;
  font-weight: ${font.bold};
`;

const PaidAmountText = styled.Text`
  font-size: 13px;
  font-weight: ${font.bold};
  color: ${colors.brandDark};
  margin-right: 6px;
`;

const PaidDateText = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-right: 8px;
`;

const PaidMethodText = styled.Text`
  font-size: 11px;
  color: ${colors.inkMuted};
  font-weight: ${font.semibold};
  margin-right: 8px;
`;

const PaidCheckCircle = styled.View`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  background-color: ${colors.brand};
  align-items: center;
  justify-content: center;
`;

const PaidCheck = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const EmptySection = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  padding: 12px 0;
`;

const BottomSpacer = styled.View`
  height: 40px;
`;

/* ── screen ──────────────────────────────────────────── */

type FeeWithId = Fee & { id: string };

export default function MatchFeesScreen() {
  const { matchId } = useLocalSearchParams<{ matchId: string }>();
  const { user } = useAuth();
  const { club } = useClub();

  const [match, setMatch] = useState<(Match & { id: string }) | null>(null);
  const [matchLoading, setMatchLoading] = useState(true);
  const [fees, setFees] = useState<FeeWithId[]>([]);
  const [feesLoading, setFeesLoading] = useState(true);
  const [markingId, setMarkingId] = useState<string | null>(null);

  const { confirmProps, confirm } = useConfirm();
  const { alertProps, alert } = useAlert();

  // Listen to match doc
  useEffect(() => {
    if (!club?.id || !matchId) return;
    const unsub = onSnapshot(
      doc(db, "clubs", club.id, "matches", matchId),
      (snap) => {
        if (snap.exists()) {
          setMatch({ id: snap.id, ...snap.data() } as Match & { id: string });
        }
        setMatchLoading(false);
      }
    );
    return unsub;
  }, [club?.id, matchId]);

  // Real-time listener on fees subcollection
  useEffect(() => {
    if (!club?.id || !matchId) return;
    const unsub = onSnapshot(
      collection(db, "clubs", club.id, "matches", matchId, "fees"),
      (snap) => {
        const list: FeeWithId[] = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as FeeWithId[];
        setFees(list);
        setFeesLoading(false);
      }
    );
    return unsub;
  }, [club?.id, matchId]);

  const [paymentSheetFee, setPaymentSheetFee] = useState<FeeWithId | null>(null);

  const handleMarkPaid = (fee: FeeWithId) => {
    setPaymentSheetFee(fee);
  };

  const confirmMarkPaid = async (method: PaymentMethod) => {
    const fee = paymentSheetFee;
    if (!fee || !club?.id || !matchId || !user?.uid) return;

    setPaymentSheetFee(null);
    setMarkingId(fee.id);
    try {
      const feeRef = doc(
        db,
        "clubs",
        club.id,
        "matches",
        matchId,
        "fees",
        fee.id
      );
      await updateDoc(feeRef, {
        paid: true,
        paidAt: Timestamp.now(),
        markedBy: user.uid,
        paymentMethod: method,
      });
      await alert({
        title: "Fee marked as paid",
        emoji: "\u2705",
        message: `${fee.displayName}'s ${pence(fee.amount)} fee recorded as ${method === "cash" ? "cash" : "bank transfer"}.`,
      });
    } catch (err: any) {
      await alert({
        title: "Error",
        emoji: "\uD83D\uDE15",
        message: err.message || "Failed to update fee.",
        buttonColor: colors.danger,
      });
    } finally {
      setMarkingId(null);
    }
  };

  if (matchLoading || feesLoading) {
    return (
      <Center>
        <ActivityIndicator size="large" color={colors.brand} />
      </Center>
    );
  }

  if (!match) {
    return (
      <Center>
        <EmptyText>Match not found.</EmptyText>
      </Center>
    );
  }

  const unpaid = fees.filter((f) => !f.paid);
  const paid = fees.filter((f) => f.paid);
  const totalFees = fees.length;
  const paidCount = paid.length;
  const collected = paid.reduce((sum, f) => sum + f.amount, 0);
  const outstanding = unpaid.reduce((sum, f) => sum + f.amount, 0);
  const progressPct = totalFees > 0 ? (paidCount / totalFees) * 100 : 0;

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Match header */}
        <MatchHeader>
          <TeamBadge>
            <TeamBadgeText>{match.team}</TeamBadgeText>
          </TeamBadge>
          <MatchTitle>vs {match.opponent}</MatchTitle>
          <MatchMeta>{formatDate(match.date)}</MatchMeta>
        </MatchHeader>

        {/* Summary */}
        {totalFees > 0 && (
          <SummaryCard>
            <SummaryText>
              <SummaryBold>
                {paidCount}/{totalFees}
              </SummaryBold>{" "}
              paid{" \u00B7 "}
              <SummaryBold>{pence(collected)}</SummaryBold> collected
              {" \u00B7 "}
              <SummaryBold>{pence(outstanding)}</SummaryBold> outstanding
            </SummaryText>
            <ProgressBarOuter>
              <ProgressBarInner pct={progressPct} />
            </ProgressBarOuter>
          </SummaryCard>
        )}

        {/* Unpaid section */}
        <Section>
          <SectionLabel>Unpaid ({unpaid.length})</SectionLabel>
          {unpaid.length > 0 ? (
            <Card>
              {unpaid.map((fee, i) => (
                <Row key={fee.id} hasBorder={i < unpaid.length - 1}>
                  <Avatar bg={colors.amber50}>
                    <AvatarText color={colors.amber}>
                      {fee.displayName?.[0]?.toUpperCase()}
                    </AvatarText>
                  </Avatar>
                  <Flex1>
                    <NameText>{fee.displayName}</NameText>
                  </Flex1>
                  <AmountBadge>
                    <AmountBadgeText>{pence(fee.amount)}</AmountBadgeText>
                  </AmountBadge>
                  <Pressable
                    onPress={() => handleMarkPaid(fee)}
                    disabled={markingId === fee.id}
                    style={({ pressed }) => ({ opacity: pressed ? 0.8 : 1 })}
                  >
                    <MarkPaidBtn>
                      {markingId === fee.id ? (
                        <ActivityIndicator size="small" color="#fff" />
                      ) : (
                        <MarkPaidBtnText>Mark paid</MarkPaidBtnText>
                      )}
                    </MarkPaidBtn>
                  </Pressable>
                </Row>
              ))}
            </Card>
          ) : (
            <EmptySection>Everyone has paid!</EmptySection>
          )}
        </Section>

        {/* Paid section */}
        <Section>
          <SectionLabel>Paid ({paid.length})</SectionLabel>
          {paid.length > 0 ? (
            <Card>
              {paid.map((fee, i) => (
                <Row key={fee.id} hasBorder={i < paid.length - 1}>
                  <Avatar bg={colors.brand50}>
                    <AvatarText color={colors.brandDark}>
                      {fee.displayName?.[0]?.toUpperCase()}
                    </AvatarText>
                  </Avatar>
                  <Flex1>
                    <NameText>{fee.displayName}</NameText>
                  </Flex1>
                  <PaidAmountText>{pence(fee.amount)}</PaidAmountText>
                  <PaidMethodText>
                    {fee.paymentMethod === "cash" ? "Cash" : fee.paymentMethod === "bank_transfer" ? "Bank" : "Stripe"}
                  </PaidMethodText>
                  <PaidCheckCircle>
                    <PaidCheck>{"\u2713"}</PaidCheck>
                  </PaidCheckCircle>
                </Row>
              ))}
            </Card>
          ) : (
            <EmptySection>No payments yet</EmptySection>
          )}
        </Section>

        {/* Empty state */}
        {totalFees === 0 && (
          <Section>
            <EmptyText>No fee records for this match yet.</EmptyText>
          </Section>
        )}

        <BottomSpacer />
      </ContentWidth>

      {/* Payment method picker */}
      <Modal visible={!!paymentSheetFee} animationType="slide" transparent>
        <ModalContent onClose={() => setPaymentSheetFee(null)}>
          <PaymentMethodSheet>
            <SheetTitle>
              How did {paymentSheetFee?.displayName} pay?
            </SheetTitle>
            <SheetSubtitle>
              {paymentSheetFee ? pence(paymentSheetFee.amount) : ""} match fee
            </SheetSubtitle>
            <Pressable
              onPress={() => confirmMarkPaid("cash")}
              style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
            >
              <PaymentOption>
                <PaymentOptionEmoji>💵</PaymentOptionEmoji>
                <PaymentOptionLabel>Cash</PaymentOptionLabel>
              </PaymentOption>
            </Pressable>
            <Pressable
              onPress={() => confirmMarkPaid("bank_transfer")}
              style={({ pressed }) => ({ opacity: pressed ? 0.9 : 1 })}
            >
              <PaymentOption>
                <PaymentOptionEmoji>🏦</PaymentOptionEmoji>
                <PaymentOptionLabel>Bank transfer</PaymentOptionLabel>
              </PaymentOption>
            </Pressable>
            <Pressable
              onPress={() => setPaymentSheetFee(null)}
              style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
            >
              <SheetCancel>Cancel</SheetCancel>
            </Pressable>
          </PaymentMethodSheet>
        </ModalContent>
      </Modal>

      <ConfirmSheet {...confirmProps} />
      <AlertSheet {...alertProps} />
    </Screen>
  );
}

/* ── Payment method sheet styles ── */

const PaymentMethodSheet = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 24px;
  padding-bottom: 40px;
`;

const SheetTitle = styled.Text`
  font-size: 18px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  text-align: center;
  margin-bottom: 4px;
`;

const SheetSubtitle = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  text-align: center;
  margin-bottom: 20px;
`;

const PaymentOption = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 14px;
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding: 16px;
  margin-bottom: 10px;
`;

const PaymentOptionEmoji = styled.Text`
  font-size: 24px;
`;

const PaymentOptionLabel = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const SheetCancel = styled.Text`
  font-size: 15px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-align: center;
  padding: 14px;
  margin-top: 4px;
`;
